import React from "react";
import { Navigate, Route } from "react-router";
import { apiDocsPlugin, ApiExplorerPage } from "@backstage/plugin-api-docs";
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from "@backstage/plugin-catalog";
import {
  CatalogImportPage,
  catalogImportPlugin,
} from "@backstage/plugin-catalog-import";
import { ScaffolderPage, scaffolderPlugin } from "@backstage/plugin-scaffolder";
import { SearchPage } from "@backstage/plugin-search";
import { TechRadarPage } from "@backstage/plugin-tech-radar";
import {
  DefaultTechDocsHome,
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from "@backstage/plugin-techdocs";
import { UserSettingsPage } from "@backstage/plugin-user-settings";
import { apis } from "./apis";
import { entityPage } from "./components/catalog/EntityPage";
import { searchPage } from "./components/search/SearchPage";
import { Root } from "./components/Root";
import { orgPlugin } from "@backstage/plugin-org";
import {
  catalogGraphPlugin,
  CatalogGraphPage,
} from "@backstage/plugin-catalog-graph";
import * as plugins from "./plugins";
import { EntitySentryContent } from "plugin-sentry";
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInProviderConfig,
  SignInPage,
} from "@backstage/core-components";
import { TechDocsAddons } from "@backstage/plugin-techdocs-react";
import { Mermaid } from "@internal/backstage-plugin-techdocs-addon-mermaid";
import { ReportIssue } from "@backstage/plugin-techdocs-module-addons-contrib";
import { oktaAuthApiRef } from "@backstage/core-plugin-api";
import { FlatRoutes } from "@backstage/core-app-api";
import { createApp } from "@backstage/app-defaults";
import { EnvPage, CodeDisplayPage } from "@internal/plugin-code-concerns";
import { RouteDisplayPage } from "plugin-route-display";
import { EntityGithubPullRequestsContent } from "@roadiehq/backstage-plugin-github-pull-requests";
import { EntityGithubActionsContent } from "@backstage/plugin-github-actions";
import { EntityGithubInsightsContent } from "@roadiehq/backstage-plugin-github-insights";
import {
  GithubIssuesCard,
  GithubIssuesPage,
} from "@backstage/plugin-github-issues";
import {
  CurrentUserFieldExtension,
  ScaledObjectListFieldExtension,
  ListFieldExtension,
  EnvVarFieldExtension,
} from "@internal/plugin-scaffolder-field-extensions";
import { ScaffolderFieldExtensions } from "@backstage/plugin-scaffolder";
import { OpsReviewPage, OwnershipEditor } from "@internal/plugin-ops-review";
import { CustomCatalogPage } from "./components/CatalogPage";
import { radiatorLightTheme, radiatorDarkTheme } from "@internal/plugin-theme";
import { AdminPanelPage } from "@internal/plugin-admin-panel";
import type { IdentityApi } from "@backstage/core-plugin-api";
import { discoveryApiRef, useApi } from "@backstage/core-plugin-api";
import { setTokenCookie } from "./utils/cookieAuth";
import { OpsgeniePage } from "@internal/plugin-opsgenie-api";
import { AppRouter } from "@backstage/core-app-api";

const oktaProvider: SignInProviderConfig = {
  id: "okta-auth-provider",
  title: "Okta",
  message: "Sign in using Okta",
  apiRef: oktaAuthApiRef,
};

const app = createApp({
  apis,
  // @ts-ignore
  plugins: Object.values(plugins),
  themes: [radiatorLightTheme, radiatorDarkTheme],
  components: {
    SignInPage: (props) => {
      const discoveryApi = useApi(discoveryApiRef);

      return (
        <SignInPage
          {...props}
          auto
          provider={oktaProvider}
          onSignInSuccess={async (identityApi: IdentityApi) => {
            setTokenCookie(
              await discoveryApi.getBaseUrl("cookie"),
              identityApi
            );
            props.onSignInSuccess(identityApi);
          }}
        />
      );
    },
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      // @ts-ignore
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    bind(catalogGraphPlugin.externalRoutes, {
      // @ts-ignore
      catalogEntity: catalogPlugin.routes.catalogEntity,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Navigate key="/" to="catalog?filters[kind]=component&filters[user]=all" />
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogPage
        tableOptions={{
          showTitle: true,
          searchFieldVariant: "outlined",
          searchAutoFocus: true,
          searchFieldStyle: {
            width: "100%",
            background: "#FFFBC8",
            color: "black",
          },
        }}
      />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DefaultTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      {/* @ts-ignore */}
      <TechDocsAddons>
        <Mermaid />
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      {/* @ts-ignore */}
      <ScaffolderFieldExtensions>
        <CurrentUserFieldExtension />
        <ScaledObjectListFieldExtension />
        <ListFieldExtension />
        <EnvVarFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route path="/import" element={<CatalogImportPage />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/opsgenie" element={<OpsgeniePage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/env-feature-flags" element={<EnvPage />} />
    <Route path="/graph" element={<CatalogGraphPage />} />
    <Route path="/code" element={<CodeDisplayPage />} />
    <Route path="/route-display" element={<RouteDisplayPage />} />
    <Route
      path="/pull-requests"
      element={<EntityGithubPullRequestsContent />}
    />
    <Route path="/github-actions" element={<EntityGithubActionsContent />} />
    <Route path="/code-insights" element={<EntityGithubInsightsContent />} />
    <Route path="/github-issues-card" element={<GithubIssuesCard />} />
    <Route path="/github-issues-page" element={<GithubIssuesPage />} />
    <Route path="/sentry" element={<EntitySentryContent />} />
    <Route path="/ops-review" element={<OpsReviewPage />} />
    <Route path="/ownership-editor" element={<OwnershipEditor />} />
    <Route path="/admin-panel" element={<AdminPanelPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay transientTimeoutMs={2500} />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>
);
